import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Lock } from "lucide-react";
import { EASE } from "./Reveal";

/**
 * Browser chrome wrapper — the signature device of the DataPulse Web page.
 *
 * The address bar is the artifact every client actually receives, so it is the
 * one element the page is built around. Used twice: autoplaying in the hero,
 * and driven by domain tabs in the work section.
 *
 * @param {string}  url    Address bar text (already sliced when typing).
 * @param {boolean} caret  Show the blinking caret after the text.
 * @param {string}  phase  'typing' | 'loading' | 'loaded' — drives the progress line and live badge.
 */
export function BrowserFrame({
  url,
  caret = false,
  phase = "loaded",
  children,
  className = "",
  // When true the viewport grows to fill a flex parent instead of holding an
  // aspect ratio, so a pinned section can size the frame to the screen.
  fill = false,
}) {
  const reduce = useReducedMotion();
  const isLive = phase === "loaded";

  return (
    <div
      className={`overflow-hidden rounded-xl border border-[#1A2A44] bg-[#0B1423] shadow-[0_24px_70px_-28px_rgba(0,0,0,0.9)] ${
        fill ? "flex min-h-0 flex-col" : ""
      } ${className}`}
    >
      {/* Chrome bar */}
      <div className="relative flex items-center gap-3 border-b border-[#1A2A44] bg-[#0E1929] px-3 py-2.5 sm:px-4">
        {/* Window controls — monochrome so the frame reads as designed, not as a stock mac mockup */}
        <div className="hidden shrink-0 items-center gap-1.5 sm:flex" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-[#22334E]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#22334E]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#22334E]" />
        </div>

        {/* Address bar */}
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-[#1A2A44] bg-[#060B14] px-2.5 py-1.5">
          <Lock className="h-3 w-3 shrink-0 text-[#4C6A94]" aria-hidden="true" />
          <span className="dp-mono truncate text-[11px] tracking-tight text-[#B6C6DE] sm:text-xs">
            {url}
          </span>
          {caret && !reduce && (
            <motion.span
              className="h-3.5 w-[1.5px] shrink-0 bg-[#FF6A3D]"
              animate={{ opacity: [1, 1, 0, 0] }}
              transition={{ duration: 0.9, repeat: Infinity, times: [0, 0.5, 0.5, 1] }}
              aria-hidden="true"
            />
          )}
        </div>

        {/* Live badge */}
        <div className="flex shrink-0 items-center gap-1.5 pl-0.5">
          <span className="relative flex h-1.5 w-1.5">
            {isLive && !reduce && (
              <motion.span
                className="absolute inset-0 rounded-full bg-[#FF6A3D]"
                animate={{ scale: [1, 2.6], opacity: [0.55, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
              />
            )}
            <span
              className="relative h-1.5 w-1.5 rounded-full transition-colors duration-300"
              style={{ backgroundColor: isLive ? "#FF6A3D" : "#3A4E6E" }}
            />
          </span>
          <span
            className="dp-mono hidden w-[22px] text-[10px] uppercase tracking-[0.14em] text-[#7E93B2] transition-opacity duration-300 sm:inline"
            style={{ opacity: isLive ? 1 : 0 }}
          >
            live
          </span>
        </div>

        {/* Load progress line */}
        <motion.span
          className="absolute bottom-0 left-0 h-[2px] bg-[#FF6A3D]"
          initial={false}
          animate={
            phase === "loading"
              ? { width: "100%", opacity: 1 }
              : phase === "typing"
                ? { width: "0%", opacity: 0 }
                : { width: "100%", opacity: 0 }
          }
          transition={{ duration: phase === "loading" ? 0.6 : 0.3, ease: EASE }}
          aria-hidden="true"
        />
      </div>

      {/* Viewport */}
      <div className={`relative bg-[#060B14] ${fill ? "min-h-0 flex-1" : ""}`}>{children}</div>
    </div>
  );
}

// Cycle timings, in ms. Kept together so the pace is tunable in one place.
const HOLD = 1700; // how long a loaded site sits before the next one is typed
const BACKSPACE_PER_CHAR = 13;
const TYPE_PER_CHAR = 38;
const BEAT = 180; // pause on the completed URL before it "navigates"
const LOAD = 520; // the loading sweep

/**
 * Drives the hero's address bar: hold → backspace → type the next domain →
 * load → hold. Pauses when off-screen or hovered, and collapses to a static
 * first frame under prefers-reduced-motion.
 *
 * Starts on a random site so a refresh doesn't always open on the same one.
 *
 * @param {Array<{domain: string}>} sites        Stable array reference.
 * @param {boolean}                 active       Whether the animation should be running.
 * @param {boolean}                 randomStart  Pick the opening site at random. Off when
 *   the frame is static (mobile), so the opening screenshot is a known file that
 *   index.html can preload — a random LCP image is an unpreloadable one.
 */
export function useAddressBar(sites, active, randomStart = true) {
  const reduce = useReducedMotion();
  // One seed drives both bits of initial state so they can't disagree.
  const [seed] = useState(() => (randomStart ? Math.floor(Math.random() * sites.length) : 0));
  const [index, setIndex] = useState(seed);
  const [typed, setTyped] = useState(sites[seed].domain);
  const [phase, setPhase] = useState("loaded");

  useEffect(() => {
    if (reduce || !active) return undefined;

    let cancelled = false;
    const timers = new Set();

    const wait = (ms) =>
      new Promise((resolve) => {
        const id = setTimeout(() => {
          timers.delete(id);
          resolve();
        }, ms);
        timers.add(id);
      });

    (async () => {
      // Read the current slide once; the loop owns the index from here on.
      let i = index;

      while (!cancelled) {
        setPhase("loaded");
        await wait(HOLD);
        if (cancelled) return;

        // Backspace the current domain
        setPhase("typing");
        const current = sites[i].domain;
        for (let c = current.length; c >= 0; c -= 1) {
          if (cancelled) return;
          setTyped(current.slice(0, c));
          await wait(BACKSPACE_PER_CHAR);
        }

        // Type the next one
        i = (i + 1) % sites.length;
        const next = sites[i].domain;
        for (let c = 1; c <= next.length; c += 1) {
          if (cancelled) return;
          setTyped(next.slice(0, c));
          await wait(TYPE_PER_CHAR);
        }
        if (cancelled) return;

        // Beat on the full URL, then swap the screenshot behind the loading sweep
        await wait(BEAT);
        if (cancelled) return;
        setPhase("loading");
        setIndex(i);
        await wait(LOAD);
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // `index` is seed-only state, read once when the loop starts. Including it
    // would tear down and restart the loop on every slide change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce, active, sites]);

  return { index, typed, phase, caret: phase === "typing" };
}

export default BrowserFrame;
